import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';
import { flatMap as _flatMap, flatten as _flatten, chunk as _chunk } from 'lodash';
import {
	DataQuerySorting,
	GetAllRoutesMonitoring,
	RoutesMonitoringQuery,
	RoutesMonitoringResponse,
	Task,
	DataQueryPagination
} from '@bringg/types';

import RootStore from 'bringg-web/stores/root-store';
import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import { FilterQueryObject } from '../top-section/types';
import { fetchFieldsProvider } from '../../shared/utils/fetch-task-fields/fetch-fields-provider';

export const ROUTES_LIMIT = 150;

const mapQueryParams = async (
	query: FilterQueryObject & { from: string; to: string }
): Promise<RoutesMonitoringQuery> => {
	await getRoot<RootStore>().data.teamsStore.fetchAll();

	const { ids, driver_ids, by_title_ids, team_ids, status, from, to } = query;

	const teamIds = !team_ids?.length ? getRoot<RootStore>().data.teamsStore.all.map(({ id }) => id) : team_ids;

	const mappedQuery: RoutesMonitoringQuery = {
		ids,
		driverIds: driver_ids,
		plannedRouteIds: by_title_ids,
		teamIds,
		timeWindow: {
			start: from,
			end: to
		}
	};

	if (status !== undefined) {
		mappedQuery[status] = true;
	}

	Object.keys(mappedQuery).forEach(key => {
		if (mappedQuery[key] === undefined) {
			delete mappedQuery[key];
		}
	});

	return mappedQuery;
};

const CHUNK_SIZE = 150;

const splitTasksToChunks = (routes: RoutesMonitoringResponse[], size = CHUNK_SIZE): number[][] => {
	return _chunk(
		_flatMap(routes, r => r.task_ids),
		size
	);
};

const fetchRoutes = async (
	filterQuery: FilterQueryObject & { from: string; to: string },
	options: {
		from?: number;
		to?: number;
		orderByDirection?: DataQuerySorting['direction'];
		signal?: AbortSignal;
	}
): Promise<{
	routes: GetAllRoutesMonitoring;
	hasNextPage: boolean;
	prevId?: number;
	nextId?: number;
}> => {
	await getRoot<RootStore>().data.teamsStore.fetchAll();

	const query = await mapQueryParams(filterQuery);

	const pagination: DataQueryPagination = {
		// + 1 to check if there is a next page
		limit: ROUTES_LIMIT + 1
	};

	if (options.from) {
		pagination.from = options.from;
	}
	if (options.to) {
		pagination.to = options.to;
	}

	// Order by timeWindow to get the oldest runs first
	const sorting: DataQuerySorting[] = [
		{
			property: 'timeWindow',
			direction: options.orderByDirection || 'asc'
		},
		{
			property: 'ids',
			direction: options.orderByDirection || 'asc'
		}
	];

	let routes = await getRootEnv().dashboardSdk.sdk.v2.runs.loadRunsData(
		{
			query,
			pagination,
			sorting
		},
		{ signal: options.signal }
	);

	const hasNextPage = routes.length > ROUTES_LIMIT;
	if (hasNextPage) {
		// Remove the last item as it is used to check if there is a next page
		routes = routes.slice(0, routes.length - 1);
	}

	// Reverse the order as we want to always show the oldest runs first, the order by direction is for getting the next page
	if (options.orderByDirection === 'desc') {
		routes.reverse();
	}

	return {
		routes,
		hasNextPage,
		prevId: routes[0]?.id,
		nextId: routes[routes.length - 1]?.id
	};
};

const fetchTasksByIds = async (ids: number[], options: { signal?: AbortSignal } = {}) => {
	return getRootEnv().dashboardSdk.sdk.v2.tasks.loadMany(ids, fetchFieldsProvider.get(), {
		useCache: true,
		...options
	});
};

const fetchTasks = async (
	routes: GetAllRoutesMonitoring,
	options: {
		signal?: AbortSignal;
	} = {}
): Promise<Map<number, Map<number, Task>>> => {
	const tempTasksByRun = new Map();
	const runsInChunks = splitTasksToChunks(routes);

	try {
		const response = await Promise.all(runsInChunks.map(async chunk => fetchTasksByIds(chunk, options)));

		const tasks = _flatten(response);
		const customerIdsToFetch = [];

		tasks.forEach(task => {
			if (task) {
				const waypoint = getActionableWaypoint(task);
				waypoint.customer_id && customerIdsToFetch.push(waypoint.customer_id);

				if (!tempTasksByRun.has(task.run_id)) {
					tempTasksByRun.set(task.run_id, new Map());
				}

				tempTasksByRun.get(task.run_id).set(task.id, task);
			}
		});

		if (customerIdsToFetch.length) {
			await getRootEnv().dashboardSdk.sdk.v2.customers.loadMany(customerIdsToFetch, {
				useCache: true,
				chunkSize: CHUNK_SIZE,
				signal: options.signal
			});
		}

		return tempTasksByRun;
	} catch (err) {
		console.error(`failed to fetch tasks, ${err}`);
	}
};

const fetchPlannedRoutes = async (routes: GetAllRoutesMonitoring, options?: { signal?: AbortSignal }) => {
	const plannedRouteIds = [...new Set(routes.filter(r => !!r.planned_route_id).map(r => r.planned_route_id))];

	const idsToFetch = plannedRouteIds.reduce((acc, plannedRouteId) => {
		if (!getRoot<RootStore>().data.plannedRoutesStore.get(plannedRouteId)) {
			acc.push(plannedRouteId);
		}
		return acc;
	}, []);

	await getRoot<RootStore>().data.plannedRoutesStore.batchGet(idsToFetch, options);
};

export const routesMonitoringLoaderProvider = {
	routes: fetchRoutes,
	tasks: fetchTasks,
	plannedRoutes: fetchPlannedRoutes
};
